import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../components-v2/Combobox';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../components/UserCombobox';
import { XIconNew } from '../../../components/svgs/icons/XIcon';
import { getDisplayName } from '../../../../models/NameUtils';
import { styleSheet } from '../styles';

interface IProps {
	/** The logged-in (or impersonated) user — the implicit "me" default. */
	currentUser: Api.IUser | null;
	/** Currently selected user (equals currentUser when defaulted to "me"). */
	selectedUser: Api.IUser | null;
	/** Receives the picked user, or null when cleared (caller resets to "me"). */
	onChange: (user: Api.IUser | null) => void;
	/** Admins with scheduler reporting may search/pick any user; others are locked to "me". */
	canBrowseAll: boolean;
}

/**
 * Searchable single-user picker for the calendar. Shows one user's calendar at a time and
 * defaults to the current user ("Me"); the selected person is shown as the input placeholder.
 * When someone other than the logged-in user is selected, an in-input clear (✕) resets back
 * to "me" (not everyone). Non-admins get a disabled control fixed to themselves.
 */
export const CalendarUserSelect: React.FC<IProps> = ({ currentUser, selectedUser, onChange, canBrowseAll }) => {
	const inputId = React.useId();
	const isMe = !selectedUser || selectedUser.id === currentUser?.id;
	// The selected name lives in the placeholder so it's always visible when the input is
	// empty. We intentionally do NOT pass `id` to UserCombobox: doing so makes it re-fill
	// the input with the selected user the moment you clear it, which blocks typing a new
	// search. base-ui fills the input with the picked user's name on selection instead.
	const selectionLabel = selectedUser ? `${getDisplayName(selectedUser)}${isMe ? ' (Me)' : ''}` : 'Me';

	// base-ui's built-in clear only shows when the combobox owns a non-null value; we manage
	// the selection in the parent (no `id`), so we render our own in-field ✕ instead.
	const showClear = canBrowseAll && !isMe;

	return (
		<div className={css(styleSheet.userSelect)}>
			{/* Keyed by selection so picking a user (or clearing via ✕) resets the input. */}
			<UserCombobox key={selectedUser?.id ?? 'me'} onValueChange={onChange}>
				<ComboboxInput id={`${inputId}-calendar-user`} placeholder={selectionLabel} disabled={!canBrowseAll} />
				<ComboboxContent styles={[styleSheet.userSelectDropdown]}>
					<UserComboboxStatus />
					<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
				</ComboboxContent>
			</UserCombobox>
			{showClear ? (
				<Button
					type='button'
					variant='ghost'
					size='icon'
					styles={[styleSheet.userSelectClear]}
					onClick={() => onChange(null)}
					aria-label='Show my calendar'
					title='Show my calendar'
				>
					<XIconNew width={12} height={12} />
				</Button>
			) : null}
		</div>
	);
};
